import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { getPublishImpact, publishFlow } from "../../services/flowBuilders.api";

/**
 * Publicar el borrador (0422 R4).
 *
 * Antes de publicar se dice a quién afecta: las conversaciones que están ahora mismo en este
 * flujo siguen con la versión nueva, y las que esperan en un paso que el borrador ya no tiene se
 * reiniciarán con su próximo mensaje (el backend las libera al publicar). Con errores del
 * revisor no se publica: el simulador ya se bloquea con ellos y producción merece lo mismo.
 */
const PublishFlowDialog = ({ open, flowId, blockingErrors, onClose, onPublished }) => {
  const [impacto, setImpacto] = useState(null);
  const [publicando, setPublicando] = useState(false);

  useEffect(() => {
    if (!open || !flowId) return;
    setImpacto(null);
    getPublishImpact(flowId)
      .then(({ data }) => setImpacto(data))
      .catch(() => setImpacto({ enCurso: null, seReinician: null }));
  }, [open, flowId]);

  const publicar = async () => {
    setPublicando(true);
    try {
      const { data } = await publishFlow(flowId);
      toast.success(
        data?.liberadas
          ? `Publicado. ${data.liberadas} conversación(es) en pasos borrados se reiniciarán con su próximo mensaje.`
          : "Publicado: las conversaciones ya usan esta versión."
      );
      onPublished && onPublished();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setPublicando(false);
    }
  };

  return (
    <Dialog open={open} onClose={publicando ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Publicar cambios</DialogTitle>
      <DialogContent>
        {blockingErrors ? (
          <Typography variant="body2" style={{ color: "#e53935" }}>
            El revisor encontró errores que bloquean el flujo. Corrígelos antes de publicar.
          </Typography>
        ) : !impacto ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <Typography variant="body2" gutterBottom>
              Las conversaciones empezarán a usar esta versión en cuanto publiques.
            </Typography>
            {impacto.enCurso === null ? (
              <Typography variant="body2" color="textSecondary">
                No se pudo comprobar cuántas conversaciones hay en curso.
              </Typography>
            ) : (
              <Typography variant="body2">
                Ahora mismo hay <b>{impacto.enCurso}</b> conversación(es) en este flujo.
                {impacto.seReinician > 0 && (
                  <span style={{ color: "#fb8c00" }}>
                    {" "}<b>{impacto.seReinician}</b> espera(n) en un paso que ya no existe: se reiniciará(n) con su próximo mensaje.
                  </span>
                )}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={publicando}>Cancelar</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={publicar}
          disabled={publicando || blockingErrors || !impacto}
        >
          {publicando ? <CircularProgress size={18} /> : "Publicar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishFlowDialog;
